import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { importDraftsApi, type BomCategory, type ImportDraftDetail } from "../api/structure";
import { assetTypesApi, type AssetTypeView } from "../api/catalog";
import type { ApiError } from "../api/client";
import {
  IMPORT_MAX_POSITIONS, applyEdits, categoryCounts, collapsedFrom, editsOf, setCategoryIncluded, summarize,
  toImportRequest, toImportState, type ImportState,
} from "../lib/structureImport";
import { formatDateTime } from "../lib/dates";
import { usePositionKinds } from "../lib/usePositionKinds";
import { useConfirm, usePrompt } from "../components/ConfirmDialog";
import { PermLink } from "../components/PermLink";
import { PositionKindIcon } from "../components/PositionKindIcon";
import { PositionTreePicker } from "../components/PositionTreePicker";
import { useStructureScope } from "../lib/structureScope";
import type { SelectOption } from "../components/Select";
import { useToast } from "../components/Toast";
import Ic from "../components/icons.jsx";
import { ImportTree, categoryLabel } from "./structure/ImportTree";
import { importErrorText } from "./structure/importErrors";

const CATEGORIES: BomCategory[] = ["OTHER_ID", "NO_ID"];

/** Levels shown open when a draft is opened: the machine, its assemblies and what is directly in them. */
const OPEN_LEVELS = 2;

/** Quiet time after the last change before the draft is written. */
const SAVE_DELAY_MS = 1000;

type SaveState = "saved" | "pending" | "saving" | "error" | "stale";
type Target = { id: string; name: string };

/**
 * `/struktura/uvoz/:id` — one draft of the structure import (sprint 5j §3a,
 * 2026-10-20): the bill of materials read from the file, reviewed over as many
 * days as it takes ("this yes, this no", names, kinds, where it goes) and then
 * written under the chosen position. Every change is written by itself a
 * moment after the last one; an imported draft is only shown.
 */
export default function StructureImportPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const { kinds, holdsAssetOf } = usePositionKinds();
  const scope = useStructureScope();
  // Every part type once, for the "Tip dela" list of every row (2026-10-20).
  const [types, setTypes] = useState<AssetTypeView[]>([]);
  useEffect(() => {
    let cancelled = false;
    assetTypesApi.list({ size: 1000, sort: "name" })
      .then((page) => { if (!cancelled) setTypes(page.content); })
      .catch(() => { if (!cancelled) setTypes([]); });
    return () => { cancelled = true; };
  }, []);

  const [draft, setDraft] = useState<ImportDraftDetail | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // The file's proposal; the draft stores only what differs from it.
  const [base, setBase] = useState<ImportState | null>(null);
  const [state, setState] = useState<ImportState | null>(null);
  const [name, setName] = useState("");
  const [toTopLevel, setToTopLevel] = useState(false);
  const [target, setTarget] = useState<Target | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<ApiError | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ---- saving ---------------------------------------------------------------
  const version = useRef(0);
  const latest = useRef({ name, state, target, toTopLevel, base });
  useEffect(() => { latest.current = { name, state, target, toTopLevel, base }; });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const again = useRef(false);
  // Deleted or imported: nothing is written any more.
  const gone = useRef(false);

  useEffect(() => {
    let cancelled = false;
    importDraftsApi.get(id)
      .then((d) => {
        if (cancelled) return;
        const proposal = toImportState(d.preview);
        const current = applyEdits(proposal, d.edits);
        setDraft(d);
        setBase(proposal);
        setState(current);
        setName(d.name);
        setToTopLevel(d.toTopLevel);
        setTarget(d.targetParentId && d.targetName && !d.targetMissing ? { id: d.targetParentId, name: d.targetName } : null);
        setCollapsed(collapsedFrom(current, OPEN_LEVELS));
        setSavedAt(d.updatedAt);
        setSaveState("saved");
        setError(null);
        version.current = d.version;
      })
      .catch((err: ApiError) => { if (!cancelled) setLoadError(err); });
    return () => { cancelled = true; };
  }, [id, reloadKey]);

  /** Writes what the screen holds; one request at a time — a change made meanwhile is written right after. */
  const saveNow = useCallback((): Promise<boolean> => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (inFlight.current) { again.current = true; return inFlight.current; }
    const run = (async () => {
      setSaveState("saving");
      try {
        do {
          again.current = false;
          const cur = latest.current;
          if (gone.current || !cur.state || !cur.base) return false;
          const saved = await importDraftsApi.update(id, {
            name: cur.name,
            edits: editsOf(cur.state, cur.base),
            targetParentId: cur.toTopLevel ? null : cur.target?.id ?? null,
            toTopLevel: cur.toTopLevel,
            version: version.current,
          });
          version.current = saved.version;
          setSavedAt(saved.updatedAt);
        } while (again.current);
        setSaveState(timer.current ? "pending" : "saved");
        setError(null);
        return true;
      } catch (err) {
        const e = err as ApiError;
        setSaveState(e.code === "DRAFT_STALE" ? "stale" : "error");
        setError(e);
        return false;
      } finally {
        inFlight.current = null;
      }
    })();
    inFlight.current = run;
    return run;
  }, [id]);

  const saveRef = useRef(saveNow);
  useEffect(() => { saveRef.current = saveNow; }, [saveNow]);

  /** Called after every change made on the screen. */
  const changed = useCallback(() => {
    if (gone.current) return;
    setSaveState((s) => (s === "stale" ? s : "pending"));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; void saveRef.current(); }, SAVE_DELAY_MS);
  }, []);

  // Leaving the screen writes what is still waiting.
  useEffect(() => () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; void saveRef.current(); }
  }, []);

  // Closing the tab while something is not written yet asks first.
  useEffect(() => {
    if (saveState !== "pending" && saveState !== "saving") return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [saveState]);

  // ---- changes --------------------------------------------------------------
  function edit(next: ImportState) {
    setState(next);
    changed();
  }

  function pickTarget(next: Target) {
    setTarget(next);
    changed();
  }

  function setTopLevel(next: boolean) {
    setToTopLevel(next);
    changed();
  }

  function commitName() {
    const next = nameDraft.trim();
    setRenaming(false);
    if (!next || next === name) return;
    setName(next);
    changed();
  }

  function toggleCollapsed(itemId: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  }

  const kindOptions = useMemo<SelectOption<string>[]>(
    () => kinds.map((k) => ({ value: k.code, label: k.name, left: <PositionKindIcon kind={k.code} /> })),
    [kinds],
  );
  const typeOptions = useMemo<SelectOption<string>[]>(
    () => types.map((ty) => ({ value: ty.id, label: ty.name, keywords: ty.code })),
    [types],
  );
  const summary = useMemo(() => (state ? summarize(state) : null), [state]);
  // The groups the file has lines in, for the "Odjednom" buttons.
  const groups = useMemo(
    () => (state ? CATEGORIES.map((category) => ({ category, ...categoryCounts(state, category) })).filter((g) => g.total > 0) : []),
    [state],
  );

  const readOnly = draft?.status === "IMPORTED";
  const tooMany = summary !== null && summary.positions > IMPORT_MAX_POSITIONS;
  const noTarget = !toTopLevel && target === null;
  // The first level is outside every branch of a structure scope (sprint 7 §12); a draft may still point there.
  const topLevelOutOfScope = toTopLevel && scope.limited;
  const blocked = summary === null || summary.selected === 0 || summary.incomplete > 0 || tooMany || noTarget || topLevelOutOfScope || saveState === "stale";

  async function submit() {
    if (!state || !summary || blocked || submitting || readOnly) return;
    const where = toTopLevel ? t("structureImport.topLevel", "prvi nivo strukture") : `„${target!.name}“`;
    const ok = await confirm({
      title: t("structureImport.confirmTitle", "Ubaciti u strukturu?"),
      message: t("structureImport.confirm", "Pravi se {{count}} pozicija ispod: {{where}}. Posle ubacivanja pozicije se menjaju i brišu u Strukturi, jedna po jedna ili po grani.", { count: summary.positions, where }),
      confirmLabel: t("structureImport.submit", "Ubaci u strukturu"),
    });
    if (!ok) return;
    setSubmitting(true);
    setError(null);
    // The draft keeps exactly the decisions that were imported.
    if (!(await saveNow())) { setSubmitting(false); return; }
    try {
      const result = await importDraftsApi.run(id, toImportRequest(state, toTopLevel ? null : target!.id));
      gone.current = true;
      toast.success(t("structureImport.done", "Ubačeno {{count}} pozicija.", { count: result.created }));
      navigate(`/struktura/${result.rootIds[0]}`);
    } catch (err) {
      setError(err as ApiError);
      setSubmitting(false);
    }
  }

  async function removeDraft() {
    const reason = await prompt({
      title: t("structureImport.deleteTitle", "Brisanje nacrta"),
      message: t("structureImport.deleteMessage", "Obrisati nacrt „{{name}}“? U strukturi se ništa ne menja.", { name }),
      confirmLabel: t("action.delete", "Obriši"),
      placeholder: t("deleteReason.placeholder", "Razlog brisanja (obavezno) — npr. rashodovano, zamenjeno, greška pri unosu…"),
      multiline: true,
      required: true,
      danger: true,
    });
    if (!reason) return;
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    gone.current = true;
    try {
      await importDraftsApi.remove(id, reason);
      toast.success(t("structureImport.deleted", "Nacrt „{{name}}“ je obrisan.", { name }));
      navigate("/struktura/uvoz");
    } catch (err) {
      gone.current = false;
      setError(err as ApiError);
    }
  }

  if (loadError) {
    return (
      <div>
        <div className="page-head">
          <div><h1>{t("structureImport.title", "Uvoz strukture iz Excela")}</h1></div>
          <div className="page-actions">
            <Link className="btn" to="/struktura/uvoz" style={{ textDecoration: "none" }}><Ic.list /> {t("structureImport.backToList", "Pregled uvoza")}</Link>
          </div>
        </div>
        <section className="card"><div style={{ ...errorBox, margin: 18 }} role="alert">{importErrorText(t, loadError)}</div></section>
      </div>
    );
  }

  if (!draft || !state || !summary) {
    return <section className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></section>;
  }

  return (
    <div>
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          {renaming ? (
            <input
              className="input draft-name-input"
              value={nameDraft}
              maxLength={200}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the field replaces the title the user just chose to rename
              autoFocus
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitName(); }
                if (e.key === "Escape") { e.preventDefault(); setRenaming(false); }
              }}
              aria-label={t("structureImport.draftName", "Naziv nacrta")}
            />
          ) : (
            <h1 className="draft-title">
              <span>{name}</span>
              {readOnly
                ? <span className="tag ok">{t("structureImport.status.imported", "Uvezen")}</span>
                : <span className="tag info">{t("structureImport.status.draft", "Nacrt")}</span>}
            </h1>
          )}
          <div className="sub draft-sub">
            <span>{t("structureImport.draftSub", "Fajl „{{file}}“ · napravio {{who}}, {{when}}", { file: draft.fileName, who: draft.createdByName ?? "—", when: formatDateTime(draft.createdAt) })}</span>
            {!readOnly && <SaveIndicator state={saveState} savedAt={savedAt} onRetry={() => { void saveNow(); }} />}
          </div>
        </div>
        <div className="page-actions">
          <Link className="btn" to="/struktura/uvoz" style={{ textDecoration: "none" }}><Ic.list /> {t("structureImport.backToList", "Pregled uvoza")}</Link>
          {!readOnly && !renaming && (
            <button type="button" className="btn" onClick={() => { setNameDraft(name); setRenaming(true); }}>
              <Ic.pencil /> {t("structureImport.renameDraft", "Preimenuj")}
            </button>
          )}
          {!readOnly && (
            <button type="button" className="btn" style={dangerBtn} onClick={() => { void removeDraft(); }}>
              {t("structureImport.delete", "Obriši nacrt")}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={errorBox} role="alert">
          {importErrorText(t, error)}
          {saveState === "stale" && (
            <button type="button" className="link-btn" style={{ marginLeft: 8 }} onClick={() => setReloadKey((k) => k + 1)}>
              {t("structureImport.reload", "Učitaj ponovo")}
            </button>
          )}
        </div>
      )}

      {readOnly && (
        <section className="card" style={{ marginBottom: 16 }}>
          <div className="card-b draft-imported">
            <span>
              {t("structureImport.importedLine", "Uvezeno {{when}} · {{who}} · {{count}} pozicija. Nacrt se više ne menja.", {
                when: formatDateTime(draft.importedAt), who: draft.importedByName ?? "—", count: draft.importedCount ?? 0,
              })}
            </span>
            {draft.importRootId && (
              <PermLink permission="structure:view" className="btn sm" to={`/struktura/${draft.importRootId}`} style={{ textDecoration: "none" }}>
                <Ic.tree /> {t("structureImport.showInStructure", "Prikaži u strukturi")}
              </PermLink>
            )}
          </div>
        </section>
      )}

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-h" style={{ gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ overflowWrap: "anywhere" }}>{draft.fileName}</h3>
            <div className="import-stats" aria-live="polite">
              <span>{t("structureImport.stat.items", "U fajlu")} <b>{summary.items}</b></span>
              <span>{t("structureImport.stat.selected", "Označeno")} <b>{summary.selected}</b></span>
              <span>{t("structureImport.stat.positions", "Pravi se pozicija")} <b>{summary.positions}</b></span>
            </div>
          </div>
        </div>
        <div className="card-b">
          <div className="import-tools">
            <button type="button" className="btn sm" onClick={() => setCollapsed(new Set())}><Ic.arrowDown /> {t("structure.expandAll", "Raširi sve")}</button>
            <button type="button" className="btn sm" onClick={() => setCollapsed(collapsedFrom(state, 0))}><Ic.arrowUp /> {t("structure.collapseAll", "Skupi sve")}</button>
            {!readOnly && groups.length > 0 && (
              <>
                <span className="muted" style={{ fontSize: 12.5 }}>{t("structureImport.bulk", "Odjednom:")}</span>
                {groups.map(({ category, total, included }) => {
                  const all = included === total;
                  return (
                    <button key={category} type="button" className={`chip${all ? " active" : ""}`} aria-pressed={all} onClick={() => edit(setCategoryIncluded(state, category, !all))}>
                      {categoryLabel(t, category)} <span className="chip-n">{included}/{total}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
          {!readOnly && (
            <small className="muted" style={{ display: "block", marginTop: 10 }}>
              {t("structureImport.help", "Kvačica odlučuje šta se ubacuje; skidanjem kvačice sa sklopa ide cela grana. Naziv se menja klikom na njega, vrsta u listi desno. Stavka sa količinom × N pravi N pozicija.")}
            </small>
          )}
        </div>
      </section>

      <section className="card" style={{ marginBottom: 16 }}>
        <ImportTree
          state={state}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
          onChange={edit}
          kindOptions={kindOptions}
          typeOptions={typeOptions}
          holdsAsset={holdsAssetOf}
          readOnly={readOnly}
        />
      </section>

      {!readOnly && (
        <section className="card">
          <div className="card-h"><h3>{t("structureImport.where", "Gde se ubacuje")}</h3></div>
          <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(!scope.limited || toTopLevel) && (
              <label className="check-row">
                <input type="checkbox" checked={toTopLevel} onChange={(e) => setTopLevel(e.target.checked)} />
                {t("structure.modal.toTopLevel", "Na prvi nivo (bez roditelja)")}
              </label>
            )}
            {!toTopLevel && (
              <div className="field">
                <span>{t("structureImport.target", "Pozicija ispod koje ide mašina")}</span>
                <PositionTreePicker value={target?.id ?? null} onChange={(n) => pickTarget({ id: n.id, name: n.name })} scoped />
              </div>
            )}
            {topLevelOutOfScope && (
              <span className="filter-row-hint is-err" role="alert">{t("structureImport.error.topLevelOutOfScope", "Prvi nivo je van vašeg opsega izmena strukture — skinite kvačicu i izaberite poziciju u svom opsegu.")}</span>
            )}
            <span className="muted" style={{ fontSize: 12.5 }}>
              {toTopLevel
                ? t("structureImport.targetTop", "Mašina ide na prvi nivo strukture.")
                : target
                  ? t("structureImport.targetLabel", "Mašina ide ispod: {{name}}", { name: target.name })
                  : draft.targetMissing
                    ? t("structureImport.error.target", "Izabrana pozicija više ne postoji — izaberi drugu.")
                    : t("structure.modal.noTarget", "Nije izabran cilj.")}
            </span>
            {summary.incomplete > 0 && (
              <span className="filter-row-hint is-err" role="alert">{t("structureImport.incomplete", "Označenih stavki bez vrste ili naziva: {{count}}. Dopuni ih ili im skini kvačicu.", { count: summary.incomplete })}</span>
            )}
            {tooMany && (
              <span className="filter-row-hint is-err" role="alert">{t("structureImport.error.tooLarge", "Previše pozicija za jedan uvoz (najviše {{limit}}). Skini deo stavki.", { limit: IMPORT_MAX_POSITIONS })}</span>
            )}
            <div className="form-actions">
              <Link className="btn lg" to="/struktura/uvoz" style={{ textDecoration: "none" }}>{t("structureImport.later", "Nastavi kasnije")}</Link>
              <button type="button" className="btn primary lg" onClick={() => { void submit(); }} disabled={blocked || submitting}>
                {submitting
                  ? t("action.saving", "Čuvanje…")
                  : t("structureImport.submitCount", "Ubaci u strukturu ({{count}})", { count: summary.positions })}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/** "Sačuvano …" / "Čuva se…" / "Nije sačuvano — Pokušaj ponovo" next to the draft's title. */
function SaveIndicator({ state, savedAt, onRetry }: { state: SaveState; savedAt: string | null; onRetry: () => void }) {
  const { t } = useTranslation();
  if (state === "pending" || state === "saving") {
    return <span className="draft-save" aria-live="polite">{t("structureImport.saving", "Čuva se…")}</span>;
  }
  if (state === "error") {
    return (
      <span className="draft-save is-err" role="status">
        {t("structureImport.notSaved", "Nije sačuvano")}
        <button type="button" className="link-btn" onClick={onRetry}>{t("structureImport.retry", "Pokušaj ponovo")}</button>
      </span>
    );
  }
  if (state === "stale") {
    return <span className="draft-save is-err" role="status">{t("structureImport.staleShort", "Nacrt je u međuvremenu menjan")}</span>;
  }
  return (
    <span className="draft-save" aria-live="polite">
      <Ic.check aria-hidden="true" /> {t("structureImport.saved", "Sačuvano {{when}}", { when: formatDateTime(savedAt) })}
    </span>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
